/**
 * How a plucked-string note is voiced: which cached buffer it plays, and what
 * is done to it at playback to make it this note rather than the last one.
 *
 * Pure, so the property that matters is tested (pluck.test.ts): **the number
 * of distinct buffers is bounded by the number of pitches**, however long the
 * ambience runs.
 *
 * It was not. The buffer cache was keyed by pitch, length AND damping, and the
 * Olympian kithara draws a random length and damping for every note - so no two
 * notes ever shared a key, and each rendered and kept a fresh ~0.6MB buffer.
 * A heap snapshot after a long session on Table & Cards showed 1,483 of them,
 * ~860MB held by one Map, and on an iPhone Safari killed the page.
 *
 * So the buffer is rendered once per pitch at the longest length and a fixed
 * damping, and the per-note variety moves to playback, where it costs nothing
 * to keep: length becomes a fade-out and stop on the shared buffer, damping
 * becomes a low-pass cutoff (a less-damped string is a brighter one).
 */

/** Long enough for every caller: the kithara draws 2.4-3.6s, the low octave uses 3.4s. */
export const PLUCK_RENDER_SECONDS = 3.6;

/** The damping every buffer is rendered with; per-note damping is a filter now. */
export const PLUCK_RENDER_DAMPING = 0.996;

/**
 * A backstop, not the mechanism: the key is pitch alone, so a scale of 12 notes
 * plus its octave below settles at 24 entries. If a future caller ever passes
 * pitches from a continuous range, the cache is dropped and rebuilt rather than
 * allowed to grow without limit again.
 */
export const PLUCK_CACHE_MAX = 64;

export interface PluckVoice {
  /** Cache key for the rendered buffer. */
  key: string;
  /** Seconds after the start at which the fade-out begins. */
  fadeFrom: number;
  /** Seconds after the start at which the note has faded to silence and stops. */
  stopAt: number;
  /** Low-pass cutoff, Hz: brighter for a less-damped string. */
  cutoffHz: number;
}

export function pluckVoice(freq: number, seconds: number, damping: number): PluckVoice {
  const length = Math.min(PLUCK_RENDER_SECONDS, Math.max(0.2, seconds));
  // 0.994 (dull, short) .. 0.997 (bright, ringing) onto a cutoff that never
  // drops below the fundamental's first few harmonics.
  const t = Math.min(1, Math.max(0, (damping - 0.994) / 0.003));
  const cutoffHz = Math.max(freq * 4, 1600 + t * 5400);
  return {
    key: String(Math.round(freq)),
    // The last 12% fades, which is what the rendered buffer's own tail did.
    fadeFrom: length * 0.88,
    stopAt: length,
    cutoffHz: Math.round(cutoffHz),
  };
}
